import { useLocation, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useSmartBriefs } from "@/api/smart-briefs";
import { LoadingPageState } from "@/components/loading-page-state";
import { useMerchant } from "@/providers/MerchantProvider";
import type { smartBriefsRoute } from "@/router";
import { ProductTheaterView } from "./components/ProductTheaterView";
import { SmartBriefEditModal } from "./components/SmartBriefEditModal";
import { SmartBriefsListPage } from "./SmartBriefsListPage";
import { SmartProductBriefsEmptyState } from "./smart-product-briefs-empty-state";

/**
 * SmartBriefsRouter - handles routing between landing page and list page.
 *
 * View and Edit are now handled via modals in SmartBriefsList, not separate routes.
 * This simplifies navigation and keeps the user context on the list page.
 */
export const SmartBriefsRouter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const merchant = useMerchant();
  const { data: briefs = [], isLoading } = useSmartBriefs();

  // Get URL params for pre-selected product
  const { productId, productName } = useSearch({ strict: false }) as ReturnType<(typeof smartBriefsRoute)["useSearch"]>;

  // Theater state - lifted here to survive page transitions
  const [showTheater, setShowTheater] = useState(false);
  const [canCancelTheater, setCanCancelTheater] = useState(true);
  const [editBriefId, setEditBriefId] = useState<string | null>(null);
  const cancelCallbackRef = useRef<(() => void) | null>(null);

  // Track if we've processed the pre-selected product
  const hasProcessedPreselection = useRef(false);

  const handleNavigateToLanding = () => {
    navigate({ to: "/merchant/$merchantId/smart-briefs", params: { merchantId: merchant } });
  };

  // Show theater from child components
  const handleShowTheater = () => {
    setCanCancelTheater(true);
    setShowTheater(true);
  };

  // Called when brief generation completes - store the brief ID
  const handleBriefGenerated = (briefId: string) => {
    // Clear the cancel callback since generation completed successfully
    cancelCallbackRef.current = null;
    setEditBriefId(briefId);
  };

  // Called when theater closes (either by timeout or cancel)
  const handleTheaterClose = () => {
    // Call the cancel callback if registered (cancels the timer in ProductSelectionModal)
    if (cancelCallbackRef.current) {
      cancelCallbackRef.current();
      cancelCallbackRef.current = null;
    }
    setShowTheater(false);
    // Edit modal will show automatically since editBriefId is set
  };

  // Register a cancel callback from ProductSelectionModal
  const registerCancelCallback = (callback: () => void) => {
    cancelCallbackRef.current = callback;
  };

  // Close edit modal
  const handleEditModalClose = () => {
    setEditBriefId(null);
  };

  // Redirect to landing page if no briefs exist and user is on list page
  useEffect(() => {
    if (!isLoading && briefs.length === 0 && location.pathname === `/merchant/${merchant}/smart-briefs/list`) {
      navigate({ to: "/merchant/$merchantId/smart-briefs", params: { merchantId: merchant }, replace: true });
    }
  }, [briefs.length, isLoading, location.pathname, navigate, merchant]);

  if (isLoading) {
    return <LoadingPageState />;
  }

  // Clear URL params after processing preselection
  const clearPreselection = () => {
    if (productId || productName) {
      navigate({
        to: "/merchant/$merchantId/smart-briefs",
        params: { merchantId: merchant },
        search: {},
        replace: true,
      });
    }
    hasProcessedPreselection.current = true;
  };

  // Theater callbacks to pass to child components
  const theaterProps = {
    onShowTheater: handleShowTheater,
    onBriefGenerated: handleBriefGenerated,
    onTheaterClose: handleTheaterClose,
    canCancel: canCancelTheater,
    setCanCancel: setCanCancelTheater,
    registerCancelCallback,
    // Pre-selected product from URL params
    preselectedProductId: !hasProcessedPreselection.current ? productId : undefined,
    preselectedProductName: !hasProcessedPreselection.current ? productName : undefined,
    onPreselectionProcessed: clearPreselection,
  };

  // Render content based on path
  let content: React.ReactNode;

  if (briefs.length === 0) {
    // First run: the full-bleed empty state replaces the whole page body.
    content = <SmartProductBriefsEmptyState theaterProps={theaterProps} />;
  } else {
    // Show list
    content = <SmartBriefsListPage onNavigateToLanding={handleNavigateToLanding} theaterProps={theaterProps} />;
  }

  return (
    <>
      {/* Theater View - shown during brief generation */}
      <ProductTheaterView isOpen={showTheater} onClose={handleTheaterClose} canCancel={canCancelTheater} />

      {/* Edit Modal - shown after brief generation */}
      {editBriefId && (
        <SmartBriefEditModal isOpen={!!editBriefId} onClose={handleEditModalClose} briefId={editBriefId} />
      )}

      {/* Page content */}
      {content}
    </>
  );
};
